import { useQuery } from "react-query";

import { Sort, useSort } from "@/components/atoms";
import { LocalFilterObjectType, LocalFilterValueType } from "@/shared/contexts";
import { FeatureItem, FeaturePagination, PageParams, SortOrder, TypedCommonPagedRequestParams } from "@/shared/domain";

import useCategories from "./useCategories";
import usePagination from "./usePagination";

export type usePaginationFromEndpointProps<ObjectType, SortPropertyType> = {
    result: ObjectType[];
    total: number;
    totalOfPages: number;
    page: PageParams;
    category: string;
    isFetched: boolean;
    isFetching: boolean;
    orderBy: Sort<SortPropertyType>;
    handleSort: (property: SortPropertyType) => void;
    setPage: (value: number) => void;
    setCategory: (value: string) => void;
    handlePageSizeChange: (value: number) => void;
};

type Props<ObjectPaginationType, RequestParamsType, SortPropertyType> = {
    id?: string;
    tableMode: boolean;
    objectType: LocalFilterObjectType;
    endPoint: (request: RequestParamsType, pathCategory?: string) => Promise<ObjectPaginationType>;
    policyholdersIdList?: number[];
    defaultCategory?: string;
    defaultSorting?: Sort<SortPropertyType>;
    filters?: LocalFilterValueType[];
    otherParams?: Partial<RequestParamsType>;
};

const usePaginationFromEndpoint = <
    ObjectType extends FeatureItem,
    ObjectPaginationType extends FeaturePagination<ObjectType>,
    RequestParamsType extends TypedCommonPagedRequestParams<SortPropertyType>,
    SortPropertyType,
>({
    id,
    objectType,
    endPoint,
    tableMode,
    policyholdersIdList,
    defaultCategory = "0",
    defaultSorting = { property: undefined, order: SortOrder.Descending },
    filters,
    otherParams = {},
}: Props<ObjectPaginationType, RequestParamsType, SortPropertyType>): usePaginationFromEndpointProps<
    ObjectType,
    SortPropertyType
> => {
    const { page, changePage, changePageSize } = usePagination(tableMode);
    const [orderBy, orderByActions] = useSort<SortPropertyType>({ sort: defaultSorting });
    const { category, changeCategory } = useCategories({ defaultCategory });

    const isEnabled = !policyholdersIdList || policyholdersIdList.length > 0;
    const defaultResult = () => ({ result: [], total: 0 } as ObjectPaginationType);

    const flatFilter = {};
    if (filters) {
        filters.forEach((filter) => {
            Object.entries(filter).forEach((a) => {
                // eslint-disable-next-line prefer-destructuring
                flatFilter[a[0]] = a[1];
            });
        });
    }

    const requestParams = {
        ...page,
        ...orderByActions.getSort(),
        ...flatFilter,
        ...otherParams,
    } as RequestParamsType;

    if (policyholdersIdList) {
        requestParams.policyholdersIdList = policyholdersIdList;
    }

    const {
        data = defaultResult(),
        isFetching,
        isFetched,
    } = useQuery([`${objectType}sPaginated`, requestParams, category, id], () => endPoint(requestParams, category), {
        keepPreviousData: true,
        enabled: isEnabled,
        suspense: false,
    });

    const paginatedResults = isEnabled ? data : defaultResult();

    const totalOfPages = Math.ceil(paginatedResults.total / page.pageSize);

    return {
        total: paginatedResults.total,
        result: paginatedResults.result,
        page,
        category,
        isFetched,
        isFetching,
        totalOfPages,
        setPage: changePage,
        setCategory: changeCategory,
        handlePageSizeChange: changePageSize,
        orderBy,
        handleSort: orderByActions.updateSort,
    };
};

export default usePaginationFromEndpoint;
